import React, {
    useMemo,
    useState,
    FC,
} from 'react'
import moment from 'moment'

import DataTable from "@pixel/core/DataTable"
import Divider from "@pixel/core/Divider"
import LabelValueLayout from '@pixel/core/LabelValueLayout'
import LabelValueBase from '@pixel/core/LabelValueBase'
import Link from "@pixel/core/Link"
import {
    Avatar,
    Button,
    Typography,
} from "@pixel/core"

import usePreviewCandidateColumns from './usePreviewCandidateColumns'
import { generateViewCandidateURL, generateViewJobURL } from './PreviewCandidateModal'
import { Application, CandidateApplicationListResults } from './useApplicationList'
import { ApplicationDetailsResponse } from './useApplicationDetails'
import { useMultipleLocalizedText } from '../../../contexts/Localization/localizationHooks'

interface PreviewCandidateProps {
    activeApplicationId: string
    candidateApplication: any
    candidateApplicationsList: CandidateApplicationListResults
    applicationDetails: ApplicationDetailsResponse

    onActiveApplicationChange: (applicationId: string) => void
}
export const PreviewCandidate: FC<PreviewCandidateProps> = ({
    activeApplicationId,
    candidateApplication,
    candidateApplicationsList,
    applicationDetails,
    onActiveApplicationChange,
}) => {
    const {
        APPLICATIONS,
        APPLICANT_QUICKVIEW__0_MORE,
        LAST_UPDATED,
        COMMON_APPLICATION,
        APPLICANT_QUICKVIEW_QUICK_VIEW,
    } = useMultipleLocalizedText()

    const { columns, sortOrders, sortResults } = usePreviewCandidateColumns({ activeCandidateApplication: applicationDetails })

    const [appliedSortOrder, setAppliedSortOrder] = useState({
        sortOrder: sortOrders[0],
        direction: 'desc',
    })

    const sortedResults = useMemo(() => {
        const sorted = candidateApplicationsList.nodes.slice().sort(sortResults(appliedSortOrder.sortOrder.id, appliedSortOrder.direction))
        
        return {
            nodes: sorted,
            totalCount: sorted.length,
        }
    }, [appliedSortOrder, candidateApplicationsList])

    const onSortChange = (props: any) => {
        setAppliedSortOrder({
            sortOrder: props.newSortOrder.sortOrder,
            direction: props.newSortOrder.direction,
        })
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    { candidateApplication.applicationProfilePhoto && <Avatar src={candidateApplication.applicationProfilePhoto} /> }
                    <Link
                        variant="h2"
                        href={generateViewCandidateURL({ candidateEId: candidateApplication.EId, jobEId: candidateApplication.jobEId })}
                        sx={{ marginLeft: '5px' }}
                    >
                        {candidateApplication.fullName}
                    </Link>
                </div>
            </div>

            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', marginTop: '16px' }}>
                        <Link variant="h3" href={generateViewJobURL({ jobEId: applicationDetails.application.job.EId })}>
                            {applicationDetails.application.job.title} 
                            { applicationDetails.application.job.requisitionId && `(${applicationDetails.application.job.requisitionId})` }
                        </Link>
                        { candidateApplication.applicationCount > 1 && (
                            <Typography sx={{ marginLeft: '5px' }}>
                                {APPLICANT_QUICKVIEW__0_MORE.replace('{0}', String(candidateApplication.applicationCount - 1))}
                            </Typography> 
                        )}
                    </div>
                </div>

                <LabelValueLayout>
                    <LabelValueBase label="Source Type">{applicationDetails.application.sourceTypeName}</LabelValueBase>
                    <LabelValueBase label={LAST_UPDATED}>{moment(applicationDetails.application.lastUpdate).format(window._jvselfUser.locale.userDate.shortDate().toUpperCase())}</LabelValueBase>
                </LabelValueLayout>
            </div>

            <Typography variant="h4" sx={{ marginTop: '16px', marginBottom: '16px' }}>{APPLICATIONS}</Typography>
            <DataTable
                results={sortedResults}
                getRowID={(node: Application) => node.EId}
                columns={columns}
                sortOrders={sortOrders}
                appliedSortOrder={appliedSortOrder}
                onSort={onSortChange}
                renderActions={(node: Application) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Link href={generateViewCandidateURL({ candidateEId: node.EId, jobEId: node.job.EId })} sx={{ marginRight: '12px' }}>{COMMON_APPLICATION}</Link>
                        <Divider orientation="vertical" flexItem />
                        <Button variant="text" disabled={node.EId === activeApplicationId} onClick={() => onActiveApplicationChange(node.EId)} sx={{ marginLeft: '4px' }}>{APPLICANT_QUICKVIEW_QUICK_VIEW}</Button>
                    </div>
                )}
            />
        </>
    )
}

export default PreviewCandidate
